import { useEffect, useRef, type TouchEvent } from "react";

let lastSwipeTimestamp = 0;

export default function useSwipeBack(onBack: () => void, disabled = false) {
  const start = useRef<{ x: number; y: number } | null>(null);
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;

  const reset = () => { start.current = null; };

  useEffect(() => {
    if (disabled || typeof window === "undefined") return;

    const handleTouchStart = (event: globalThis.TouchEvent) => {
      reset();
      if (event.touches.length !== 1) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      const control = target.closest("button, a, input, textarea, select, [role='button'], [contenteditable='true']");
      if (control && !control.hasAttribute("data-swipe-back-surface")) return;
      for (
        let element: Element | null = target;
        element && element !== document.body && element !== document.documentElement;
        element = element.parentElement
      ) {
        if (element.scrollWidth > element.clientWidth && /auto|scroll/.test(window.getComputedStyle(element).overflowX)) {
          return;
        }
      }
      start.current = { x: event.touches[0].clientX, y: event.touches[0].clientY };
    };

    const handleTouchMove = (event: globalThis.TouchEvent) => {
      if (!start.current) return;
      if (event.touches.length !== 1) { reset(); return; }
      const horizontal = event.touches[0].clientX - start.current.x;
      const vertical = Math.abs(event.touches[0].clientY - start.current.y);
      if (horizontal < -16 || (vertical > 16 && vertical > Math.abs(horizontal))) reset();
    };

    const handleTouchEnd = (event: globalThis.TouchEvent) => {
      const origin = start.current;
      reset();
      if (!origin || event.touches.length || !event.changedTouches.length) return;
      const horizontal = event.changedTouches[0].clientX - origin.x;
      const vertical = Math.abs(event.changedTouches[0].clientY - origin.y);
      const isEdgeSwipe = origin.x <= 50 && horizontal >= 50 && vertical <= 50;
      const isStandardSwipe = horizontal >= 70 && vertical <= 60 && horizontal > vertical * 1.5;
      if (isEdgeSwipe || isStandardSwipe) {
        const now = Date.now();
        if (now - lastSwipeTimestamp < 600) return;
        lastSwipeTimestamp = now;
        onBackRef.current();
      }
    };

    const options = { passive: true };
    window.addEventListener("touchstart", handleTouchStart, options);
    window.addEventListener("touchmove", handleTouchMove, options);
    window.addEventListener("touchend", handleTouchEnd, options);
    window.addEventListener("touchcancel", reset, options);

    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("touchcancel", reset);
    };
  }, [disabled]);

  return {
    onTouchStart: (_event: TouchEvent<HTMLElement>) => {
      // Retained for backward compatibility
    },
    onTouchMove: (_event: TouchEvent<HTMLElement>) => {},
    onTouchEnd: (_event: TouchEvent<HTMLElement>) => {},
    onTouchCancel: reset,
  };
}
